import { useEffect, useMemo, useState } from 'react';
import {
  api, type KbChange, type KbChangeComment, type KbCitation, type KbNodeCitations, type KbRunStatus,
} from '../../lib/api';
import type { DiffLineTarget } from '../ui/diff';
import { sectionDiff, type SectionedDiff } from './review-model';

export type ReviewBusy = null | 'accept' | 'reject' | 'keepBoth' | 'edit' | 'feedback' | 'lines';

/**
 * Everything one change's review needs, behaviour-for-behaviour what the classic `ChangeCard` does:
 * the same endpoints, the same lazy loads, the same "fix in flight" gating. Kept as a hook so the new
 * card and a card inside a group share it, and so the layout can change without the rules moving.
 *
 * The one addition is **line edits**: rewriting single lines of the page from the exact-changes view.
 * They compose a new body from the change's `afterBody` and go through the same resolve-by-edit call
 * the page editor uses — so they are only offered while the live page still says exactly what the
 * change wrote. Once anything later has touched it, a line index into `afterBody` no longer points at
 * the page, and saving would silently discard the later edit; the page editor (seeded from the live
 * page) is the answer then.
 */
export function useChangeReview({
  change, projectId, active, onResolved,
}: {
  change: KbChange;
  projectId: string;
  /** Whether the card is on screen and open — nothing is fetched for a collapsed card. */
  active: boolean;
  onResolved: (message: string, changedProjectId: string) => void;
}) {
  const [busy, setBusy] = useState<ReviewBusy>(null);
  const [error, setError] = useState<string | null>(null);
  const [citations, setCitations] = useState<KbNodeCitations | null>(null);
  const [selectedCitation, setSelectedCitation] = useState<KbCitation | null>(null);

  const [editing, setEditing] = useState(false);
  const [editorLoading, setEditorLoading] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editNote, setEditNote] = useState('');

  const [feedback, setFeedback] = useState('');
  const [fixRun, setFixRun] = useState<{ runId: string; message: string } | null>(null);
  const [linkedFix, setLinkedFix] = useState<{ status: KbRunStatus } | null>(null);

  const [comments, setComments] = useState<KbChangeComment[] | null>(null);
  const [composer, setComposer] = useState<DiffLineTarget | null>(null);
  const [commentText, setCommentText] = useState('');
  const [commentBusy, setCommentBusy] = useState(false);

  /** Line edits, keyed by 0-based line of `afterBody`. An empty string removes the line. */
  const [lineEdits, setLineEdits] = useState<Record<number, string>>({});
  /** Lines added after a given 0-based line of `afterBody`. */
  const [lineInserts, setLineInserts] = useState<Record<number, string[]>>({});
  const [titleEdit, setTitleEdit] = useState<string | null>(null);
  /** Whether the live page still matches the change — checked once, on the first line edit. */
  const [lineEditsAllowed, setLineEditsAllowed] = useState<null | boolean>(null);

  useEffect(() => {
    if (!active || citations) return;
    api.kb
      .changeDetail(projectId, change.id)
      .then((d) => setCitations(d.citations))
      .catch(() => setCitations({ current: [], superseded: [] }));
  }, [active, citations, projectId, change.id]);

  useEffect(() => {
    if (!active || !change.fixRunId || change.reviewStatus !== 'PENDING' || fixRun || linkedFix) return;
    api.kb
      .run(change.projectId, change.fixRunId)
      .then((run) => setLinkedFix({ status: run.status }))
      .catch(() => {});
  }, [active, change.fixRunId, change.reviewStatus, change.projectId, fixRun, linkedFix]);

  useEffect(() => {
    if (!active || comments) return;
    api.kb
      .changeComments(projectId, change.id)
      .then(setComments)
      .catch(() => setComments([]));
  }, [active, comments, projectId, change.id]);

  const diff: SectionedDiff | null = useMemo(
    () =>
      change.kind === 'MOVE' || !(change.beforeBody || change.afterBody)
        ? null
        : sectionDiff(change.beforeBody ?? '', change.afterBody ?? ''),
    [change.kind, change.beforeBody, change.afterBody],
  );

  const isPending = change.reviewStatus === 'PENDING';
  const canKeepBoth = change.kind === 'UPDATE' || change.kind === 'MERGE';
  const fixInFlight =
    Boolean(fixRun) || Boolean(linkedFix && (linkedFix.status === 'QUEUED' || linkedFix.status === 'PROCESSING'));
  const canReview = isPending && change.kind !== 'DELETE' && !fixInFlight;
  const drafts = (comments ?? []).filter((c) => c.status === 'DRAFT');

  const act = async (which: 'accept' | 'reject' | 'keepBoth') => {
    setBusy(which);
    setError(null);
    try {
      const result = await api.kb[which](projectId, change.id);
      onResolved(result.message, change.projectId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  /** Seeds the page editor with the page as it stands now, not the change's snapshot. */
  const openEditor = async () => {
    setEditing(true);
    setError(null);
    setEditorLoading(true);
    try {
      const node = await api.kb.node(change.projectId, change.path);
      setEditTitle(node.title);
      setEditBody(node.body);
    } catch (e: any) {
      setError(e.message);
      setEditing(false);
    } finally {
      setEditorLoading(false);
    }
  };

  const saveEdit = async () => {
    setBusy('edit');
    setError(null);
    try {
      const result = await api.kb.resolveChangeByEdit(projectId, change.id, {
        title: editTitle,
        body: editBody,
        ...(editNote.trim() ? { note: editNote.trim() } : {}),
      });
      onResolved(result.message, change.projectId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const startFix = async (): Promise<boolean> => {
    setBusy('feedback');
    setError(null);
    try {
      const result = await api.kb.fixChange(projectId, change.id, feedback.trim() || undefined);
      if (result.skippedDuplicate) {
        setError(result.message);
        return false;
      }
      setFixRun(result);
      setFeedback('');
      setComments((prev) =>
        (prev ?? []).map((c) => (c.status === 'DRAFT' ? { ...c, status: 'SUBMITTED' as const, fixRunId: result.runId } : c)),
      );
      return true;
    } catch (e: any) {
      setError(e.message);
      return false;
    } finally {
      setBusy(null);
    }
  };

  const addComment = async () => {
    if (!composer || commentText.trim().length < 3) return;
    setCommentBusy(true);
    setError(null);
    try {
      const created = await api.kb.addChangeComment(projectId, change.id, {
        text: commentText.trim(),
        quote: composer.quote,
        onRemoved: composer.removed,
      });
      setComments((prev) => [...(prev ?? []), created]);
      setComposer(null);
      setCommentText('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setCommentBusy(false);
    }
  };

  const removeComment = async (commentId: string) => {
    try {
      await api.kb.deleteChangeComment(projectId, change.id, commentId);
      setComments((prev) => (prev ?? []).filter((c) => c.id !== commentId));
    } catch (e: any) {
      setError(e.message);
    }
  };

  const openComposer = (target: DiffLineTarget) => {
    setComposer(target);
    setCommentText('');
    setError(null);
  };

  // ─── line edits ─────────────────────────────────────────────────────────────

  const canEditLines = canReview && change.kind !== 'MOVE' && change.afterBody !== null && lineEditsAllowed !== false;

  /** Checks, once, that the live page is still the change's `afterBody` before any line is edited. */
  const ensureLineEditable = async (): Promise<boolean> => {
    if (lineEditsAllowed !== null) return lineEditsAllowed;
    try {
      const node = await api.kb.node(change.projectId, change.path);
      const ok = node.body === (change.afterBody ?? '') && node.title === (change.afterTitle ?? node.title);
      setLineEditsAllowed(ok);
      if (!ok) {
        setError('This page has been edited since this change — line edits would overwrite that. Use Edit to change it as it stands now.');
      }
      return ok;
    } catch (e: any) {
      setError(e.message);
      return false;
    }
  };

  const lineEditCount =
    Object.keys(lineEdits).length +
    Object.values(lineInserts).reduce((n, list) => n + list.length, 0) +
    (titleEdit !== null ? 1 : 0);

  const composedBody = (): string => {
    const source = (change.afterBody ?? '').split('\n');
    const out: string[] = [];
    source.forEach((line, i) => {
      const edited = lineEdits[i];
      if (edited === undefined) out.push(line);
      else if (edited !== '') out.push(edited);
      for (const inserted of lineInserts[i] ?? []) if (inserted.trim() !== '') out.push(inserted);
    });
    return out.join('\n');
  };

  const discardLineEdits = () => {
    setLineEdits({});
    setLineInserts({});
    setTitleEdit(null);
  };

  const saveLineEdits = async () => {
    setBusy('lines');
    setError(null);
    try {
      const result = await api.kb.resolveChangeByEdit(projectId, change.id, {
        title: titleEdit ?? change.afterTitle ?? change.node?.title ?? '',
        body: composedBody(),
        ...(editNote.trim() ? { note: editNote.trim() } : {}),
      });
      onResolved(result.message, change.projectId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  return {
    change, projectId,
    busy, error, setError,
    citations, selectedCitation, setSelectedCitation,
    diff,
    isPending, canKeepBoth, fixInFlight, canReview, drafts,
    act,
    editing, setEditing, editorLoading, editTitle, setEditTitle, editBody, setEditBody, editNote, setEditNote,
    openEditor, saveEdit,
    feedback, setFeedback, fixRun, linkedFix, startFix,
    comments, composer, setComposer, commentText, setCommentText, commentBusy,
    addComment, removeComment, openComposer,
    canEditLines, ensureLineEditable, lineEdits, setLineEdits, lineInserts, setLineInserts, titleEdit, setTitleEdit,
    lineEditCount, discardLineEdits, saveLineEdits,
  };
}

export type ChangeReview = ReturnType<typeof useChangeReview>;
